import * as React from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  type VisibilityState
} from '@tanstack/react-table';

import { useDebounce } from '#/hooks/use-debounce';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '#/components/ui/table';

import { DataTablePagination } from './data-table-pagination';
import { DataTableToolbar } from './data-table-toolbar';
import { DataTableProps } from './data-table';

import { use } from 'react';
import { getColumnVisibleState } from '../table/base-data-table';

export function DataTable<TData, TValue>({
  columns,
  data,
  onDelete,
  onDeleteValidate,
  toolbarActions,
  fetch,
  getRowClassName,
  deleteAction,
  meta = {},
  totalCount = 0,
  filterableColumns = [],
  searchableColumns = [],
  rangableColumns = [],
  initialColumnVisibility = {},
  enableRowSelection = true,
  isNotRemovePram,
  isViewOption = true,
  manualPagination = true,
  pager
}: DataTableProps<TData, TValue>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let items = data;
  let total = totalCount;
  if (fetch) {
    const { data, totalCount } = use(fetch);
    items = data;
    total = totalCount;
  }

  // Search params
  const page = searchParams?.get('page') ?? '1';
  const per_page = searchParams?.get('per_page') ?? '10';
  const sort = searchParams?.get('sort');
  const [column, order] = sort?.split('.') ?? [];
  const pageCount = Math.ceil(total / parseInt(per_page));

  // Create query string
  const createQueryString = React.useCallback(
    (params: Record<string, string | number | null>) => {
      const newSearchParams = new URLSearchParams(searchParams?.toString());

      for (const [key, value] of Object.entries(params)) {
        if (value === null) {
          newSearchParams.delete(key);
        } else {
          newSearchParams.set(key, String(value));
        }
      }

      return newSearchParams.toString();
    },
    [searchParams]
  );

  const searchParamKeys = Array.from(searchParams.keys()).filter(
    (k) => k !== 'page' && k !== 'sort'
  );
  const filterableSearchParams = searchParamKeys
    .filter((k) => {
      return filterableColumns.find((column) => column.id === k);
    })
    .map((k) => {
      return {
        id: k,
        value: searchParams.get(k)?.split('.') ?? []
      };
    });
  const searchableSearchParams = searchParamKeys
    .filter((k) => {
      return searchableColumns.find((column) => column.id === k);
    })
    .map((k) => {
      return {
        id: k,
        value: searchParams.get(k)
      };
    });
  const rangableSearchParams = searchParamKeys
    .filter((k) => {
      return rangableColumns.find((column) => column.id === k);
    })
    .map((k) => {
      return {
        id: k,
        value: searchParams.get(k)
      };
    });

  // Table states
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      ...initialColumnVisibility,
      ...getColumnVisibleState(searchParams)
    });
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    ...filterableSearchParams,
    ...searchableSearchParams,
    ...rangableSearchParams
  ]);

  // Handle server-side pagination
  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: Number(page) - 1,
      pageSize: Number(per_page)
    });

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize
    }),
    [pageIndex, pageSize]
  );

  React.useEffect(() => {
    setPagination({
      pageIndex: Number(page) - 1,
      pageSize: Number(per_page)
    });
  }, [page, per_page]);

  React.useEffect(() => {
    router.replace(
      `${pathname}?${createQueryString({
        page: pageIndex + 1,
        per_page: pageSize
      })}`
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize]);

  // Handle server-side sorting
  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: column ?? '',
      desc: order === 'desc'
    }
  ]);

  React.useEffect(() => {
    router.replace(
      `${pathname}?${createQueryString({
        page,
        sort: sorting[0]?.id
          ? `${sorting[0]?.id}.${sorting[0]?.desc ? 'desc' : 'asc'}`
          : null
      })}`
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  // Handle server-side filtering
  const debouncedSearchableColumnFilters = JSON.parse(
    useDebounce(
      JSON.stringify(
        columnFilters.filter((filter) => {
          return searchableColumns.find((column) => column.id === filter.id);
        })
      ),
      500
    )
  ) as ColumnFiltersState;

  const filterableColumnFilters = columnFilters.filter((filter) => {
    return filterableColumns.find((column) => column.id === filter.id);
  });
  const rangableColumnFilters = columnFilters.filter((filter) => {
    return rangableColumns.find((column) => column.id === filter.id);
  });

  React.useEffect(() => {
    for (const column of debouncedSearchableColumnFilters) {
      if (typeof column.value === 'string') {
        router.replace(
          `${pathname}?${createQueryString({
            page,
            [column.id]: typeof column.value === 'string' ? column.value : null
          })}`
        );
      }
    }

    let removable: any = {};
    for (const key of searchParamKeys) {
      if (
        searchableColumns.find((column) => column.id === key) &&
        !debouncedSearchableColumnFilters.find((column) => column.id === key)
      ) {
        removable[key] = null;
      }
      if (Object.keys(removable).length) {
        router.replace(
          `${pathname}?${createQueryString({
            page,
            ...removable
          })}`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(debouncedSearchableColumnFilters)]);

  React.useEffect(() => {
    for (const column of filterableColumnFilters) {
      if (typeof column.value === 'object' && Array.isArray(column.value)) {
        router.replace(
          `${pathname}?${createQueryString({
            page,
            [column.id]: column.value.join('.')
          })}`
        );
      }
    }

    let removable: any = {};
    for (const key of searchParamKeys) {
      if (
        filterableColumns.find((column) => column.id === key) &&
        !filterableColumnFilters.find((column) => column.id === key)
      ) {
        removable[key] = null;
      }
    }
    if (Object.keys(removable).length) {
      router.replace(
        `${pathname}?${createQueryString({
          page,
          ...removable
        })}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterableColumnFilters)]);

  React.useEffect(() => {
    for (const column of rangableColumnFilters) {
      if (typeof column.value === 'string') {
        router.replace(
          `${pathname}?${createQueryString({
            page,
            [column.id]: typeof column.value === 'string' ? column.value : null
          })}`
        );
      }
    }

    let removable: any = {};
    for (const key of searchParamKeys) {
      if (
        rangableColumns.find((column) => column.id === key) &&
        !rangableColumnFilters.find((column) => column.id === key)
      ) {
        removable[key] = null;
      }
      if (Object.keys(removable).length) {
        router.replace(
          `${pathname}?${createQueryString({
            page,
            ...removable
          })}`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rangableColumnFilters)]);

  const table = useReactTable({
    data: items || [],
    columns,
    pageCount: pageCount ?? -1,
    meta,
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters
    },
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: manualPagination,
    manualSorting: true,
    manualFiltering: true
  });

  return (
    <div className="w-full space-y-4 overflow-auto p-1">
      <DataTableToolbar
        table={table}
        filterableColumns={filterableColumns}
        searchableColumns={searchableColumns}
        rangableColumns={rangableColumns}
        onDelete={onDelete}
        onDeleteValidate={onDeleteValidate}
        actions={toolbarActions}
        remote={true}
        deleteAction={deleteAction}
        isNotRemovePram={isNotRemovePram}
        isViewOption={isViewOption}
      />
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={getRowClassName ? getRowClassName(row) : ''}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  데이터가 존재하지 않습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {pager && <DataTablePagination table={table} totalCount={total} />}
    </div>
  );
}
